/**
 * @file components/sidebar/SidebarFooter.jsx
 * @description User profile section at the bottom of the sidebar
 * REFACTORED: Now uses split contexts (useAuth, useConversations)
 * 
 * @component SidebarFooter
 */

import React, { useState, useEffect, useRef } from 'react';

// ---Import specific context hooks ---
import { useAuth } from '../../context/AuthContext';
import { useConversations } from '../../context/ConversationsContext';
import { useChatSession } from '../../context/ChatSessionContext';

import ConfirmationDialog from '../common/ConfirmationDialog';

import styles from '../../styles/Sidebar.module.css';
import { 
    User, 
    ChevronUp, 
    LogOut, 
    HelpCircle, 
    Settings, 
    Crown, 
    Shield,
    Activity,
    Mail, 
    CreditCard
} from 'lucide-react';

// --- Sub-components specific to the footer ---

/**
 * User profile picture component with initials or icon
 * @param {object} props
 * @param {number} props.size - Icon size
 * @param {string} props.initials - User initials to display
 */
const UserProfilePic = ({ size = 20, initials = null }) => (
    <div 
        className={styles.userProfilePic} 
        style={{ 
            width: size * 1.8, 
            height: size * 1.8,
            fontSize: size * 0.8 
        }}
    >
        {initials ? (
            <span style={{ fontWeight: 600 }}>{initials}</span>
        ) : (
            <User size={size} />
        )}
    </div>
);

/**
 * User profile menu with real user data and actions
 * @param {object} props
 * @param {function} props.closeMenu - Function to close the menu
 * @param {object} props.user - Authenticated user object
 * @param {function} props.signOut - Sign out function
 * @param {object} props.stats - User statistics
 */
const UserProfileMenu = ({ closeMenu, user, onSignOut, stats, profile, onCollapse }) => {
    const userEmail = user?.signInDetails?.loginId || user?.attributes?.email || 'Unknown User';
    const userName = user?.attributes?.name || userEmail.split('@')[0];
    const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    // Determine user plan based on custom attributes or default
    const subscriptionTier = profile?.subscriptionTier || 'free';
    const planDisplay = subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1) + ' Plan';

    // Get setViewMode to navigate
    const { setViewMode } = useChatSession();

    // Navigation handler
    const handleNavigateToSettings = () => {
        setViewMode('settings');
        if (onCollapse) onCollapse();
        closeMenu();
    };
    
    const handleSignOutClick = () => {
        closeMenu();
        onSignOut(); // This will trigger the parent's sign out confirmation
    };
    
    return (
        <div className={styles.profileMenu}>
            {/* User Info Section */}
            <div className={styles.profileMenuItem}>
                <UserProfilePic size={16} initials={userInitials} />
                <div className={styles.profileMenuUserInfo}>
                    <p title={userName}>{userName}</p>
                    <span title={userEmail}>{userEmail}</span>
                    <span className={styles.planBadge}>{planDisplay}</span>
                </div>
            </div>
            
            {/* User Stats Section */}
            <div className={styles.profileMenuStats}>
                <div className={styles.statItem}>
                    <Activity size={14} />
                    <span>{stats.conversations} Chats</span>
                </div>
                <div className={styles.statItem}>
                    <CreditCard size={14} />
                    <span>{Math.round(parseFloat(stats.creditsAvailable || 0))} Credits</span>
                </div>
            </div>
            
            {/* Menu Actions */}
            <div className={styles.profileMenuDivider}></div>
            
            {subscriptionTier === 'free' && (
                <div className={styles.profileMenuItem} onClick={() => {
                    closeMenu();
                    // TODO: Navigate to upgrade page
                    console.log('Navigate to upgrade page');
                }}>
                    <Crown size={16} /> Upgrade to Pro
                </div>
            )}
            
            <div className={styles.profileMenuItem} onClick={handleNavigateToSettings}>
                <Settings size={16} /> Settings
            </div>
            
            <div className={styles.profileMenuItem} onClick={() => {
                closeMenu();
                // TODO: Navigate to security settings
                console.log('Navigate to security settings');
            }}>
                <Shield size={16} /> Security
            </div>
            
            <div className={styles.profileMenuItem} onClick={() => {
                closeMenu();
                // TODO: Open help documentation
                window.open('https://docs.example.com', '_blank');
            }}>
                <HelpCircle size={16} /> Help & Support
            </div>
            
            <div className={styles.profileMenuDivider}></div>
            
            <div 
                className={`${styles.profileMenuItem} ${styles.profileMenuSignOut}`} 
                onClick={handleSignOutClick}
            >
                <LogOut size={16} /> Sign Out
            </div>
        </div>
    );
};

/**
 * Manages the user profile section at the bottom of the sidebar.
 * Uses split contexts for better separation of concerns
 * 
 * @param {object} props
 * @param {boolean} props.isExtended - Whether the sidebar is currently extended.
 * @param {function} props.onToggleExtend - Function to call to toggle the sidebar's extended state.
 */
const SidebarFooter = ({ isExtended, onToggleExtend, onCollapse }) => {
    const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
    const [showSignOutDialog, setShowSignOutDialog] = useState(false);
    const [isSigningOut, setIsSigningOut] = useState(false);
    const [signOutError, setSignOutError] = useState(null);
    const profileMenuRef = useRef(null);
    
    // --- Get data from specific contexts ---
    // From AuthContext
    const { 
        currentUser,
        userEmail,
        userName,
        handleSignOut,
        userProfile
    } = useAuth();
    
    // From ConversationsContext
    const { conversations } = useConversations();
    
    // Calculate user statistics
    const userStats = {
        conversations: conversations.length,
        creditsAvailable: userProfile?.usageCredits
    };
    
    // Extract user information with fallbacks
    const displayEmail = userEmail || 'Unknown User';
    const displayName = userName || displayEmail.split('@')[0];
    const userInitials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    console.log(`SidebarFooter: Rendering for user ${displayName}`);

    const handleSignOutConfirm = async () => {
    setIsSigningOut(true);
    setSignOutError(null);
    try {
        console.log('SidebarFooter: User signing out...');
        await handleSignOut();
        localStorage.clear();
        sessionStorage.clear();
        console.log('SidebarFooter: Sign out successful');
        setShowSignOutDialog(false);
    } catch (error) {
        console.error('SidebarFooter: Sign out failed:', error);
        setSignOutError('Failed to sign out. Please try again.');
    } finally {
        setIsSigningOut(false);
    }
};
    
    // Effect to close the profile menu when clicking outside of it.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setProfileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleProfileClick = () => {
        // If the sidebar is already extended, toggle the menu.
        // If it's collapsed, the primary action is to extend it.
        if (isExtended) {
            setProfileMenuOpen(prev => !prev);
        } else {
            onToggleExtend();
        }
    };

    return (
        <div ref={profileMenuRef}>
            {/* The profile menu is only rendered when it should be open. */}
            {isExtended && isProfileMenuOpen && (
                <UserProfileMenu 
                    closeMenu={() => setProfileMenuOpen(false)}
                    user={currentUser}
                    onSignOut={() => setShowSignOutDialog(true)}
                    stats={userStats}
                    profile={userProfile}
                    onCollapse={onCollapse}
                />
            )}
            
            <div className={styles.userProfile} onClick={handleProfileClick}>
                <UserProfilePic initials={userInitials} />
                {isExtended && (
                    <>
                        <div className={styles.userInfo}>
                            <p title={displayName}>{displayName}</p>
                            <span className={styles.userEmail} title={displayEmail}>
                                {displayEmail}
                            </span>
                        </div>
                        <ChevronUp 
                            size={20} 
                            className={`${styles.profileArrow} ${isProfileMenuOpen ? styles.profileArrowOpen : ''}`} 
                        />
                    </>
                )}
            </div>
            <ConfirmationDialog
                isOpen={showSignOutDialog}
                onClose={() => setShowSignOutDialog(false)}
                onConfirm={handleSignOutConfirm}
                title="Sign Out"
                message="Are you sure you want to sign out?"
                confirmText="Sign Out"
                cancelText="Cancel"
                isProcessing={isSigningOut}
                error={signOutError}
                variant="primary"
            />
        </div>
    );
};

export default SidebarFooter;